/**
 * Fills in "+N other colors" for `Overflow` colour families on collection and search grids.
 *
 * Those families are separate products that share a title, so the real colour count only
 * exists across the ten overflow collections. Walking those in Liquid is what makes the PDP
 * expensive, and doing it per card would be far worse, so the count comes from the storefront
 * suggest API instead: one request per style on the page, cached for the session.
 */
const SUGGEST_LIMIT = 10;
const CACHE_PREFIX = 'ak-color-count:';

/** @type {Map<string, Promise<number>>} */
const inFlight = new Map();

/**
 * @param {string} title
 * @returns {number | null}
 */
function readCache(title) {
  try {
    const cached = sessionStorage.getItem(CACHE_PREFIX + title);
    return cached === null ? null : Number(cached);
  } catch {
    return null;
  }
}

/**
 * @param {string} title
 * @param {number} count
 */
function writeCache(title, count) {
  try {
    sessionStorage.setItem(CACHE_PREFIX + title, String(count));
  } catch {
    /* private mode / quota — the count still renders for this page view */
  }
}

/**
 * @param {string} title
 * @returns {Promise<number>} products sharing the title, capped at the API's page size
 */
function countColors(title) {
  const cached = readCache(title);
  if (cached !== null) return Promise.resolve(cached);

  const pending = inFlight.get(title);
  if (pending) return pending;

  const url = new URL('/search/suggest.json', window.location.origin);
  url.searchParams.set('q', title);
  url.searchParams.set('resources[type]', 'product');
  url.searchParams.set('resources[limit]', String(SUGGEST_LIMIT));
  url.searchParams.set('resources[options][fields]', 'title');

  const request = fetch(url, { headers: { Accept: 'application/json' }, priority: 'low' })
    .then((response) => (response.ok ? response.json() : null))
    .then((data) => {
      const products = data?.resources?.results?.products ?? [];
      const wanted = title.trim().toLowerCase();
      const count = products.filter((product) => product.title?.trim().toLowerCase() === wanted).length;
      writeCache(title, count);
      return count;
    })
    .catch(() => 0)
    .finally(() => inFlight.delete(title));

  inFlight.set(title, request);
  return request;
}

/**
 * @param {HTMLElement} target
 */
async function fill(target) {
  const title = target.dataset.akColorGroup;
  if (!title || target.dataset.akColorCountDone === 'true') return;
  target.dataset.akColorCountDone = 'true';

  const others = (await countColors(title)) - 1;
  if (others < 1) return;

  // A style with more colours than the API returns in one page reads as the cap, which
  // still tells the shopper there is more to see.
  target.textContent = `+${others} other color${others > 1 ? 's' : ''}`;
  target.classList.remove('ak-plp-tile-card__colors--pending');
}

/* One request per style is cheap but not free, so only styles the shopper is about to
   see are looked up. */
const watcher =
  typeof IntersectionObserver === 'undefined'
    ? null
    : new IntersectionObserver(
        (entries, observer) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            observer.unobserve(entry.target);
            entry.target
              .querySelectorAll('[data-ak-color-group]:not([data-ak-color-count-done])')
              .forEach((target) => fill(/** @type {HTMLElement} */ (target)));
          });
        },
        { rootMargin: '400px 0px' }
      );

function fillAll() {
  document
    .querySelectorAll('[data-ak-color-group]:not([data-ak-color-count-done])')
    .forEach((target) => {
      // Watch the card, not the placeholder: an empty line is too small to trip an
      // intersection reliably.
      const card = target.closest('.product-grid__item, product-card');
      if (watcher && card) watcher.observe(card);
      else fill(/** @type {HTMLElement} */ (target));
    });
}

fillAll();

// Filters, sorting and infinite scroll swap the grid in via the Section Rendering API.
const grid = document.querySelector('results-list, .product-grid');
if (grid) {
  new MutationObserver(fillAll).observe(grid, { childList: true, subtree: true });
}
